import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Star, Loader2, CheckCircle2, AlertCircle, Quote } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEED_REVIEWS = [
  {
    id: "seed-1",
    name: "The Morrison Family",
    location: "Naperville, IL",
    rating: 5,
    body:
      "Zaylan walked us through every option without an ounce of pressure. We finally feel like our kids are protected — and the IUL was something no one had ever explained to us before.",
  },
  {
    id: "seed-2",
    name: "Marcus T.",
    location: "Aurora, IL",
    rating: 5,
    body:
      "Honest, knowledgeable, and patient. He shopped four carriers before recommending the one that actually fit my budget. Highest praise.",
  },
  {
    id: "seed-3",
    name: "Janelle K.",
    location: "Chicago, IL",
    rating: 5,
    body:
      "I locked in mortgage protection on my first home thanks to Zaylan. The rate was lower than my coffee budget. Cannot recommend him enough.",
  },
  {
    id: "seed-4",
    name: "Devon & Aaliyah",
    location: "Joliet, IL",
    rating: 5,
    body:
      "We've been putting this off for years. Zaylan made it painless and even helped us understand what we already had so we didn't double-pay.",
  },
];

const StarRow = ({ value = 5, size = 14, interactive = false, onChange, testIdPrefix }) => {
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  return (
    <div className="inline-flex items-center gap-1" data-testid={testIdPrefix}>
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          disabled={!interactive}
          onMouseEnter={() => interactive && setHover(i)}
          onMouseLeave={() => interactive && setHover(0)}
          onClick={() => interactive && onChange?.(i)}
          aria-label={`${i} star${i > 1 ? "s" : ""}`}
          data-testid={interactive ? `star-${i}` : undefined}
          className={interactive ? "cursor-pointer" : "cursor-default"}
        >
          <Star
            className={`transition-colors ${i <= shown ? "fill-gold text-gold" : "fill-transparent text-ink/30"}`}
            style={{ width: size, height: size }}
          />
        </button>
      ))}
    </div>
  );
};

const ReviewCard = ({ review }) => (
  <article
    className="shrink-0 w-[340px] sm:w-[380px] bg-white border border-line p-6 mr-5 last:mr-0"
    data-testid={`review-card-${review.id}`}
  >
    <div className="flex items-start justify-between gap-3 mb-3">
      <StarRow value={review.rating} size={14} testIdPrefix={`stars-${review.id}`} />
      <Quote className="w-5 h-5 text-gold/40" />
    </div>
    <p className="text-ink/85 leading-relaxed text-sm font-light">
      &ldquo;{review.body}&rdquo;
    </p>
    <div className="mt-5 pt-4 border-t border-line">
      <div className="font-display text-base text-ink leading-tight">{review.name}</div>
      {review.location && (
        <div className="eyebrow text-ink/45 text-[9px] mt-1">{review.location}</div>
      )}
    </div>
  </article>
);

const Reviews = () => {
  const [approved, setApproved] = useState([]);
  const [form, setForm] = useState({ name: "", location: "", email: "", rating: 5, body: "" });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/reviews/approved`);
        setApproved(data || []);
      } catch {
        // ignore — fall back to seeds
      }
    })();
  }, []);

  // Combine approved (front of list) with seeded testimonials so the carousel always has 6+ to scroll
  const displayList = useMemo(() => {
    const combined = [...approved];
    const need = Math.max(0, 6 - combined.length);
    return need > 0 ? [...combined, ...SEED_REVIEWS.slice(0, need)] : combined;
  }, [approved]);

  // Duplicate for seamless ticker
  const tickerList = useMemo(() => [...displayList, ...displayList], [displayList]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.body.trim() || form.body.trim().length < 10) {
      setStatus({ state: "error", msg: "Please share your name and at least a brief review (10+ characters)." });
      return;
    }
    setStatus({ state: "submitting", msg: "" });
    try {
      const payload = {
        name: form.name.trim(),
        location: form.location.trim() || null,
        email: form.email.trim() || null,
        rating: Number(form.rating),
        body: form.body.trim(),
      };
      await axios.post(`${API}/reviews`, payload);
      setStatus({
        state: "success",
        msg: "Thank you! Your review has been submitted and will appear once Zaylan approves it.",
      });
      setForm({ name: "", location: "", email: "", rating: 5, body: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      setStatus({ state: "error", msg: String(detail) });
    }
  };

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="relative bg-gradient-to-b from-cream/40 via-ivory to-cream/40 py-24 sm:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 grid-overlay opacity-20" />
      <div className="absolute top-20 right-0 w-96 h-96 bg-gold/8 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10 mb-12 sm:mb-16"
        >
          <div className="max-w-2xl">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-px bg-gold" />
              <span className="eyebrow text-gold">Client Reviews</span>
            </div>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink">
              Trusted by{" "}
              <span className="italic gold-text-gradient">families like yours.</span>
            </h2>
          </div>
          <p className="text-ink/65 text-base sm:text-lg leading-relaxed font-light max-w-md">
            Real words from real clients. Worked with me? I&apos;d love to hear about your experience —
            and help others find peace of mind through your story.
          </p>
        </motion.div>

        {/* Scrolling ticker carousel */}
        <div className="relative overflow-hidden py-2 mb-16">
          <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-ivory via-ivory/80 to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-ivory via-ivory/80 to-transparent z-10 pointer-events-none" />
          <div className="flex ticker-track" data-testid="reviews-ticker">
            {tickerList.map((r, i) => (
              <ReviewCard key={`${r.id}-${i}`} review={r} />
            ))}
          </div>
        </div>

        {/* Submit review form */}
        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9 }}
          onSubmit={submit}
          noValidate
          data-testid="review-form"
          className="relative glass p-8 sm:p-10 lg:p-12 max-w-4xl mx-auto"
        >
          <span className="absolute top-0 left-0 w-12 h-px bg-gold" />
          <span className="absolute top-0 left-0 w-px h-12 bg-gold" />
          <span className="absolute bottom-0 right-0 w-12 h-px bg-gold" />
          <span className="absolute bottom-0 right-0 w-px h-12 bg-gold" />

          <div className="flex items-center gap-4 mb-6">
            <div className="w-10 h-px bg-gold" />
            <span className="eyebrow text-gold">Leave a Review</span>
          </div>
          <h3 className="font-display text-2xl sm:text-3xl text-ink leading-tight">
            Share your <span className="italic gold-text-gradient">experience.</span>
          </h3>
          <p className="mt-3 text-ink/60 text-sm font-light">
            Reviews are personally approved by Zaylan before being displayed.
          </p>

          {/* Rating row */}
          <div className="mt-7">
            <label className="eyebrow text-ink/55 text-[10px] mb-3 block">Your Rating</label>
            <div className="flex items-center gap-4">
              <StarRow
                value={form.rating}
                size={28}
                interactive
                onChange={(v) => setForm((f) => ({ ...f, rating: v }))}
                testIdPrefix="rating-input"
              />
              <span className="font-display text-2xl text-gold">{form.rating}/5</span>
            </div>
          </div>

          <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Field id="review-name" label="Your Name" required value={form.name} onChange={update("name")} placeholder="Jane Doe" />
            <Field id="review-location" label="City, State" value={form.location} onChange={update("location")} placeholder="Chicago, IL" />
            <div className="sm:col-span-2">
              <Field id="review-email" label="Email (not displayed)" type="email" value={form.email} onChange={update("email")} placeholder="you@email.com" />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="review-body" className="eyebrow text-ink/55 text-[10px] mb-3 block">
                Your Review <span className="text-gold ml-1">*</span>
              </label>
              <textarea
                id="review-body"
                data-testid="review-body"
                rows={4}
                value={form.body}
                onChange={update("body")}
                placeholder="What was your experience like working with Zaylan?"
                className="w-full bg-transparent border-b border-line focus:border-gold text-ink py-3 outline-none transition-colors resize-none placeholder:text-ink/30 font-light"
              />
              <div className="mt-1 text-xs text-ink/40">{form.body.length}/600</div>
            </div>
          </div>

          {status.state === "error" && (
            <div data-testid="review-error" className="mt-5 flex items-start gap-2 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{status.msg}</span>
            </div>
          )}
          {status.state === "success" && (
            <div data-testid="review-success" className="mt-5 flex items-start gap-2 text-gold text-sm">
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> <span>{status.msg}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={status.state === "submitting"}
            data-testid="review-submit"
            className="mt-8 group relative inline-flex items-center justify-center gap-3 bg-ink text-gold-light px-8 py-3.5 text-sm font-semibold tracking-[0.2em] uppercase overflow-hidden disabled:opacity-50 transition-all duration-500"
          >
            <span className="absolute inset-0 bg-gold scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" />
            {status.state === "submitting" ? (
              <>
                <Loader2 className="relative w-4 h-4 animate-spin" />
                <span className="relative">Sending</span>
              </>
            ) : (
              <span className="relative group-hover:text-ink transition-colors">Submit Review</span>
            )}
          </button>
        </motion.form>
      </div>
    </section>
  );
};

const Field = ({ label, id, required, ...rest }) => (
  <div>
    <label htmlFor={id} className="eyebrow text-ink/55 text-[10px] mb-3 block">
      {label}
      {required && <span className="text-gold ml-1">*</span>}
    </label>
    <input
      id={id}
      data-testid={id}
      required={required}
      autoComplete="off"
      {...rest}
      className="w-full bg-transparent border-b border-line focus:border-gold text-ink py-3 outline-none transition-colors placeholder:text-ink/30 font-light"
    />
  </div>
);

export default Reviews;
